import { useState } from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, FileText, PlusCircle, Image, FolderTree, Tag, Sparkles, BarChart3, Settings as SettingsIcon, LogOut, Menu, X, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/posts", label: "Posts", icon: FileText },
  { to: "/admin/posts/new", label: "New Post", icon: PlusCircle },
  { to: "/admin/media", label: "Media", icon: Image },
  { to: "/admin/categories", label: "Categories", icon: FolderTree },
  { to: "/admin/tags", label: "Tags", icon: Tag },
  { to: "/admin/resources", label: "CTAs & Affiliates", icon: Sparkles },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/admin/settings", label: "Site Settings", icon: SettingsIcon },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => { await logout(); navigate("/admin/login"); };

  const SidebarInner = () => (
    <>
      <div className="flex items-center gap-2 px-5 h-[70px] border-b border-ink-700">
        <span className="w-2.5 h-2.5 rounded-full bg-brand green-glow" />
        <span className="font-display font-extrabold text-white">Lucrative<span className="text-brand">Laptop</span></span>
      </div>
      <nav className="flex-1 overflow-y-auto thin-scroll py-4 px-3 space-y-1">
        {NAV.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={() => setOpen(false)}
            data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-brand/10 text-brand border border-brand/30" : "text-slate-400 hover:text-white hover:bg-ink-800 border border-transparent"}`}
          >
            <n.icon size={17} /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-ink-700 space-y-1">
        <Link to="/" target="_blank" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-400 hover:text-white hover:bg-ink-800 transition-colors"><ExternalLink size={17} /> View site</Link>
        <button onClick={doLogout} data-testid="admin-logout" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-slate-400 hover:text-red-400 hover:bg-ink-800 transition-colors"><LogOut size={17} /> Sign out</button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-ink-950 grain flex">
      <aside className="hidden lg:flex flex-col w-64 shrink-0 bg-ink-900 border-r border-ink-700 fixed inset-y-0 z-40">
        <SidebarInner />
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="relative flex flex-col w-64 bg-ink-900 border-r border-ink-700"><SidebarInner /></aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="lg:hidden flex items-center justify-between h-[70px] px-5 border-b border-ink-700 bg-ink-900 sticky top-0 z-30">
          <button onClick={() => setOpen(true)} className="text-white"><Menu size={22} /></button>
          <span className="font-display font-bold text-white">Studio</span>
          <span className="text-xs text-slate-400 font-mono-label">{user?.name}</span>
        </header>
        <div className="p-5 lg:p-8 relative z-[2]">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
